export const PIP_TO_EVERYWHERE_FLAG = 'pip_to_everywhere_tests';
export const EVERYWHERE_TESTS_PATH = '/everywhere/tests/';
export const PIP_EXPERIMENT_PENDING_CLASS = 'pip-exp-pending';
/** Fail open to the product index if the flag is still pending. */
export const PIP_BOOT_TIMEOUT_MS = 400;

export type PipToEverywhereVariant = 'control' | 'treatment';

export const isProductsIndexPath = (pathname: string): boolean =>
  pathname === '/products' || pathname === '/products/';

export const pipVariantFromFlag = (
  value: unknown,
): PipToEverywhereVariant =>
  value === 'treatment' ? 'treatment' : 'control';

/**
 * QA override, same convention as `useMixpanelExperiment`: the flag key as a
 * query param. `?pip_to_everywhere_tests=treatment|control` skips Mixpanel.
 */
export const pipVariantOverride = (
  search: string,
): PipToEverywhereVariant | null => {
  const value = new URLSearchParams(search).get(PIP_TO_EVERYWHERE_FLAG);
  return value === 'treatment' || value === 'control' ? value : null;
};

/** Keep UTM and other query params when leaving the product index. */
export const everywhereTestsRedirect = (search: string): string => {
  const params = new URLSearchParams(search);
  params.delete(PIP_TO_EVERYWHERE_FLAG);
  const query = params.toString();
  return query ? `${EVERYWHERE_TESTS_PATH}?${query}` : EVERYWHERE_TESTS_PATH;
};

/**
 * What the blocking head script does before the body paints.
 * `redirect` navigates immediately, `hide` covers the page until the flag
 * resolves, `show` leaves the product index alone.
 */
export const pipBootAction = (
  pathname: string,
  search: string,
): 'redirect' | 'hide' | 'show' => {
  if (!isProductsIndexPath(pathname)) return 'show';
  const override = pipVariantOverride(search);
  if (override === 'treatment') return 'redirect';
  if (override === 'control') return 'show';
  return 'hide';
};

/** Inline head script. Treatment override redirects before the body is parsed. */
export const PIP_BOOT_SCRIPT = `(function(){try{var path=location.pathname;if(path!=='/products'&&path!=='/products/')return;var params=new URLSearchParams(location.search);var forced=params.get('${PIP_TO_EVERYWHERE_FLAG}');if(forced==='treatment'){params.delete('${PIP_TO_EVERYWHERE_FLAG}');var q=params.toString();location.replace('${EVERYWHERE_TESTS_PATH}'+(q?'?'+q:''));return;}if(forced==='control')return;document.documentElement.classList.add('${PIP_EXPERIMENT_PENDING_CLASS}');window.__pipExpDeadline=Date.now()+${PIP_BOOT_TIMEOUT_MS};setTimeout(function(){document.documentElement.classList.remove('${PIP_EXPERIMENT_PENDING_CLASS}');}, ${PIP_BOOT_TIMEOUT_MS});}catch(e){}})();`;

export const decidePipRedirect = (input: {
  pathname: string;
  search: string;
  variantKey: unknown;
}): { variant: PipToEverywhereVariant; redirectTo: string | null } => {
  if (!isProductsIndexPath(input.pathname)) {
    return { variant: 'control', redirectTo: null };
  }
  const variant = pipVariantFromFlag(input.variantKey);
  return {
    variant,
    redirectTo:
      variant === 'treatment' ? everywhereTestsRedirect(input.search) : null,
  };
};
